import React, { useCallback, useState } from 'react';
import Styled from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Trans } from '@lingui/react';
import VideoPlayer from '@organisms/video-player';
import { Box, Flex, Container } from '@components/layout/Grid';
import ProgramExpandableInstructors from './ProgramExpandableInstructors';
import {
  GenericProgram,
  PriceTypeKind,
  ProgramCategory,
  Video,
} from '@gql/generated';
import ProgramSimplePrice from '@organisms/ProgramSimplePrice';
import programMenu from './ProgramMenu';
import programDurationTheme from '@organisms/programs/ProgramDurationTheme';
import { hexToRgbA } from '@lib/utils';
import { Button } from '@components/ButtonV2';
import { getButtonAppearance } from '@templates/pages/program/overview/WeeklyProgramOverview';

const ProgramDurationTheme = Styled(programDurationTheme)<{
  category?: ProgramCategory;
}>`
  flex-shrink: 0;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: start;
  height: 90px;
  width: 100%;
  padding: 15px 20px;
  background: ${({ theme, category }) => {
    const color =
      category === ProgramCategory['Challenges']
        ? theme.colors.primary.green
        : theme.colors.primary.stone;
    return `linear-gradient(180deg, ${hexToRgbA(color, 1)} 0%, ${hexToRgbA(
      color,
      0
    )} 100%)`;
  }};

  z-index: 1;

  span {
    color: white;
    font-weight: 400;
  }

  svg use { fill: white; }
`;

const Wrapper = Styled(Container)`
  display: flex;
  align-items: center;
`;

const ProgramMediaBox = Styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex: 2;

  @media(min-width: ${({ theme }) => theme.viewports.tablet}px){
    max-height: 300px;
    min-width: 350px;
  }
`;

const VideoWrapper = Styled.div`
  width: 100%;
  
  @media(min-width: ${({ theme }) => theme.viewports.tablet}px){
    min-width: 350px;
  }
`;

const ImageWrapper = Styled.div`
  position: relative;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;

`;

const Image = Styled.img`
  max-width: 100%;
  min-width: 100%;
  max-height: 300px;
  object-fit: cover;


  @media(min-width: ${({ theme }) => theme.viewports.tablet}px){
    max-width: none;
  }
`;

const ProgramDetails = Styled(Box)`
  flex: 3;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  h1 {
    margin: 0;
  }
`;

const TrailerVideo = Styled(VideoPlayer)``;

const ButtonsRow = Styled(Flex)`
  flex-wrap: wrap;
  align-items: center;
  gap: 15px;
`;

export const DownloadPDF = Styled.a<{ appearance?: ProgramCategory }>`
  color: #454857;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  border: 1px solid currentcolor;
  border-radius: 5px;
  padding: 10px 20px;
  cursor: pointer;

  &:hover {
    color: ${({ appearance, theme }) =>
      appearance === ProgramCategory['Challenges']
        ? theme.colors.primary.green
        : theme.colors.primary['blue-2']};
  }
`;

export const GetStarted = Styled(Button)`
  margin: 10px 0;
`;

const ProgramMenu = Styled(programMenu)`
  margin-left: auto;
  margin-right: 0;
`;

const ProgramMedia = ({
  trailer,
  programImage,
  noTrailer,
  duration,
  themes,
  category,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);

  const onPlayStart = useCallback(() => {
    return setIsPlaying(true);
  }, []);

  return (
    <ProgramMediaBox>
      {!noTrailer && trailer && (
        <VideoWrapper data-testid="ProgramTrailer">
          {!isPlaying ? (
            <ProgramDurationTheme
              duration={duration}
              themes={themes}
              category={category}
            />
          ) : null}
          <TrailerVideo
            {...{
              video: trailer,
              thumbnailImage: trailer?.thumbnailUrl,
              videoSources: trailer?.player?.assets,
              isLimited: false,
              autoPlay: false,
              onPlayStart,
            }}
          />
        </VideoWrapper>
      )}
      {!trailer && programImage ? (
        <ImageWrapper>
          <Image src={programImage} />
        </ImageWrapper>
      ) : null}
    </ProgramMediaBox>
  );
};

type ProgramInfoProps = {
  title: string;
  description: string;
  instructors?: GenericProgram['instructors'];
};

export const ProgramInfo = (props: ProgramInfoProps) => {
  const { title, description, instructors = [] } = props;
  return (
    <>
      <h1 className="text-2xl md:text-3xl">{title}</h1>
      <p>{description}</p>
      <ProgramExpandableInstructors
        programTitle={title}
        instructors={instructors}
      />
    </>
  );
};

type Props = {
  title: string;
  description: string;
  getStartedLink: string;
  instructors?: GenericProgram['instructors'];
  price?: GenericProgram['price'];
  discount: GenericProgram['discount'];
  duration?: GenericProgram['duration'];
  themes?: GenericProgram['themes'];
  assetURL?: string;
  programImage?: string;
  trailer?: Video;
  noTrailer?: boolean;
  programCategory?: ProgramCategory;
  priceType: PriceTypeKind;
  programSlug: string;
  enableGiftPurchase: boolean;
};

const GuestProgramHeader = (props: Props) => {
  const {
    title,
    description,
    getStartedLink,
    instructors = [],
    assetURL,
    themes = [],
    duration = null,
    programImage = null,
    trailer = null,
    noTrailer = false,
    programCategory = null,
    price,
    priceType,
    programSlug,
    enableGiftPurchase,
  } = props;

  const router = useRouter();
  const { programId, lang } = router.query;

  return (
    <Wrapper
      flexDirection={['column', 'column', 'row', 'row']}
      data-testid="GuestProgramHeader"
      px={15}
      py={20}
    >
      <ProgramMedia
        {...{
          trailer,
          programImage,
          noTrailer,
          duration,
          themes,
          category: programCategory,
        }}
      />
      <ProgramDetails ml={[0, 0, 40]} py={20}>
        <ProgramInfo
          title={title}
          description={description}
          instructors={instructors}
        />
        {priceType !== PriceTypeKind['AccessCodesOnly'] &&
        priceType !== PriceTypeKind['BySubscription'] ? (
          <Flex flex={1} mt={20}>
            <ProgramSimplePrice price={price} priceType={priceType} />
          </Flex>
        ) : null}
        <ButtonsRow mt={25}>
          <GetStarted
            appearance={getButtonAppearance(programCategory)}
            href={getStartedLink}
            data-testid="get-started--btn"
          >
            {programCategory === ProgramCategory['Courses'] ? (
              <Trans id="courses_2021.get_started.join_btn" />
            ) : (
              <Trans id="programs_2021.get_started.join_btn" />
            )}
          </GetStarted>
          {assetURL ? (
            <DownloadPDF
              href={assetURL}
              target="_blank"
              data-testid="download-pdf--btn"
              appearance={programCategory}
            >
              {programCategory === ProgramCategory['Courses'] ? (
                <Trans id="course.downloadPDF" />
              ) : (
                <Trans id="program.downloadPDF" />
              )}
            </DownloadPDF>
          ) : null}
          <Flex
            flexGrow="1"
            flexShrink="1"
            alignItems="center"
            ml="auto"
            mr="0"
          >
            {enableGiftPurchase ? (
              <Button
                appearance={getButtonAppearance(programCategory)}
                href={`/${lang}/${programCategory}/${programId}/buy-gift`}
                link
              >
                <Trans id="programs_2021.get_started.buy_gift_btn" />
              </Button>
            ) : null}
            <ProgramMenu
              programCategory={programCategory}
              programId={programSlug}
              disablePurchases
            />
          </Flex>
        </ButtonsRow>
      </ProgramDetails>
    </Wrapper>
  );
};

export default GuestProgramHeader;
